import type {
  Fare,
  FareSliceDeparture,
  FareSliceOutbound,
} from "@b2bportal/air-shopping-api";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import { createSelector } from "@reduxjs/toolkit";
import type { PackageState } from "../slice";
import type { RootState } from "@/store";

export const getPackageStateByFareId = (state: PackageState) => {
  return state.packages.flights.shop.data?.packageStateByFareId;
};

export const getRelatedPackageTokenByFareId = (
  state: PackageState,
  fareId: string
) => {
  const packageStateByFareId = getPackageStateByFareId(state);
  return packageStateByFareId?.[fareId]?.token;
};

export const getFlightShopCallState = (state: PackageState) => {
  return state.packages.flights.shop.state;
};

/**
 * Get the package fare ID from a fare object.
 * In packages, the actual fareId that matches packageStateByFareId keys
 * is stored in fare.example.fare, not fare.id or fare.fareSlice.
 */
const getPackageFareId = (
  fare: Fare | FareSliceOutbound | FareSliceDeparture
): string | undefined => {
  return "example" in fare ? fare.example?.fare : fare.id;
};

/**
 * Package-aware departure flight list selector.
 *
 * Overrides core flight filtering to use package total prices (flight + hotel + car)
 * instead of flight-only prices when the price filter is active.
 */
export const getPackageFilteredAndSortedDepartureFlightList = createSelector(
  [
    (state: RootState) =>
      FlightShopSelectors.getFilteredAndSortedDepartureFlightList(state),
    (state: RootState) =>
      FlightShopSelectors.hasSetMaxPriceFilterSelector(state),
    (state: RootState) => FlightShopSelectors.allFiltersSelector(state),
    (state: RootState) => getPackageStateByFareId(state),
  ],
  (
    coreFilteredFlights,
    hasSetMaxPriceFilter,
    filters,
    packageStateByFareId
  ) => {
    // If no price filter is set, use core filtering as-is
    if (!hasSetMaxPriceFilter || !packageStateByFareId) {
      return coreFilteredFlights;
    }

    const maxPriceFilter = filters.maxPriceFilter;
    if (maxPriceFilter === Number.MAX_SAFE_INTEGER) {
      return coreFilteredFlights;
    }

    // Apply package price filtering on top of core-filtered results
    const result = coreFilteredFlights.filter((flight) => {
      // Check if ANY fare in this flight meets the package price filter
      const passingFare = flight.fares.find((fare) => {
        const fareId = getPackageFareId(fare);
        if (!fareId) {
          return false;
        }

        const packageState = packageStateByFareId[fareId];

        if (!packageState?.price?.totalPrice?.fiat) {
          // No package price available for this fare - exclude it
          return false;
        }

        const packageTotalPrice = packageState.price.totalPrice.fiat.value;
        return packageTotalPrice <= maxPriceFilter;
      });

      return !!passingFare;
    });

    return result;
  }
);

/**
 * Package-aware return flight list selector.
 *
 * Overrides core flight filtering to use package total prices (flight + hotel + car)
 * instead of flight-only prices when the price filter is active.
 */
export const getPackageFilteredAndSortedReturnFlightList = createSelector(
  [
    (state: RootState) =>
      FlightShopSelectors.getFilteredAndSortedReturnFlightList(state),
    (state: RootState) =>
      FlightShopSelectors.hasSetMaxPriceFilterSelector(state),
    (state: RootState) => FlightShopSelectors.allFiltersSelector(state),
    (state: RootState) => getPackageStateByFareId(state),
  ],
  (
    coreFilteredFlights,
    hasSetMaxPriceFilter,
    filters,
    packageStateByFareId
  ) => {
    // If no price filter is set, use core filtering as-is
    if (!hasSetMaxPriceFilter || !packageStateByFareId) {
      return coreFilteredFlights;
    }

    const maxPriceFilter = filters.maxPriceFilter;
    if (maxPriceFilter === Number.MAX_SAFE_INTEGER) {
      return coreFilteredFlights;
    }

    // Apply package price filtering on top of core-filtered results
    return coreFilteredFlights.filter((flight) => {
      // Check if ANY fare in this flight meets the package price filter
      return flight.fares.some((fare) => {
        const fareId = getPackageFareId(fare);
        if (!fareId) {
          return false;
        }

        const packageState = packageStateByFareId[fareId];

        if (!packageState?.price?.totalPrice?.fiat) {
          // No package price available for this fare - exclude it
          return false;
        }

        const packageTotalPrice = packageState.price.totalPrice.fiat.value;
        return packageTotalPrice <= maxPriceFilter;
      });
    });
  }
);
